import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import ReferralsListPage from './pages/ReferralsListPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import FreePuzzlePage from './pages/FreePuzzlePage';
import MessagesPage from './pages/MessagesPage';
import AppLoginComplete from './pages/AppLoginComplete';
import ClubPage from './pages/ClubPage';
import MaintenancePage from './pages/MaintenancePage';
import { canRenderAdminInline, getAdminPanelUrl, getRootEntryPath } from './lib/siteRouting';
import { shouldShowMaintenanceScreen } from './lib/maintenance';
import { REFERRAL_QUERY_PARAM } from './lib/referral';
import './App.css';

// Redirect target for the root route that carries a `?ref=` code forward onto
// the resolved entry path, so a referral link pointing at `/` (`/?ref=<id>`)
// does not lose the code when we replace the URL. The code is also captured to
// localStorage at startup (see index.js); this keeps the visible URL correct.
const getRootRedirectTarget = () => {
  const base = getRootEntryPath();
  try {
    const ref = new URLSearchParams(window.location.search || '').get(REFERRAL_QUERY_PARAM);
    if (ref && ref.trim() && !base.includes('?')) {
      return `${base}?${REFERRAL_QUERY_PARAM}=${encodeURIComponent(ref.trim())}`;
    }
  } catch {
    /* fall through to the plain base path */
  }
  return base;
};

const AdminRouteBridge = () => {
  const location = useLocation();

  useEffect(() => {
    if (!canRenderAdminInline()) {
      window.location.replace(getAdminPanelUrl(location.pathname));
    }
  }, [location.pathname]);

  if (!canRenderAdminInline()) {
    return null;
  }

  return <AdminPanel />;
};

function App() {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (shouldShowMaintenanceScreen(pathname)) {
    return <MaintenancePage />;
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to={getRootRedirectTarget()} replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/app-login-complete" element={<AppLoginComplete />} />
          <Route path="/free-puzzle" element={<FreePuzzlePage />} />
          {/* Hidden Telegram ad landing page — reachable by direct URL only,
              not linked anywhere on the site. Meta ads point here. */}
          <Route path="/club" element={<ClubPage />} />
          <Route path="/referrals" element={<ReferralsListPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:otherUserId" element={<MessagesPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/harday-admin" element={<Navigate to="/admin" replace />} />
          <Route path="/admin" element={<AdminRouteBridge />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
